// Nutrition resolver + rollup.
//
// Single source of truth for "what are the macros of this thing?" —
// used by ItemCard, recipe previews, and the MealDetail totals card.
// Keep every render path flowing through `resolveNutrition` / `
// recipeNutrition` so the data-source hierarchy stays consistent and
// we don't accidentally double-count between UI surfaces.
//
// Source hierarchy (highest signal first; Phase 1 ships tiers 3+4+5,
// tiers 1+2 plug in during Phase 2/4):
//
//   1. pantryRow.nutritionOverride           — user typed this jar's label.
//   2. brand_nutrition[(canonicalId, brand)] — OFF / user-contributed.
//   3. ingredient_info[canonicalId].nutrition — generic canonical.
//   4. ingredients.js canonical.nutrition     — in-code fallback.
//   5. null → UI renders "tap to add".
//
// `per` schemes the registry uses:
//   "100g"    → base unit in canonical's ladder is grams; divide by 100.
//   "count"   → each item carries the nutrition (eggs, apples).
//   "serving" → use nutrition.serving_g to scale grams.

import { findIngredient, CUT_NUTRITION, STATE_NUTRITION, DEFAULT_CUT_PER_HUB, CANONICAL_ALIASES, inferCanonicalFromName } from "../data/ingredients";
import { convertWithBridge, effectiveCountWeightG, isMassLadder } from "./unitConvert";
import { normalizeUnitId } from "./units/aliases";
import { resolveNameToCanonicalId } from "./recipePairing";

// Resolve a recipe ingredient's canonical id via the same alias-map →
// fuzzy chain pairRecipeIngredients uses. Lets legacy social-imported
// recipes (parse-recipe-text era before client-side canonical stamping
// landed) still light up nutrition off the ingredient text alone.
function resolveIngredientCanonical(ing) {
  if (!ing) return null;
  if (ing.ingredientId) return ing.ingredientId;
  const name = ing.item || ing.name || "";
  if (!name) return null;
  return inferCanonicalFromName(name) || resolveNameToCanonicalId(name) || null;
}

// Resolve the best available nutrition for a pantry row. Phase 1 only
// uses tiers 3 + 4 (canonical); the optional `brandNutrition` map hooks
// in during Phase 2 and `pantryRow.nutritionOverride` during Phase 4.
// Caller passes `getInfo` from useIngredientInfo() to avoid re-reading
// the context inside every list render.
export function resolveNutrition(pantryRow, { brandNutrition, getInfo } = {}) {
  if (!pantryRow) return { nutrition: null, source: null };
  // 1. Pantry-row override (Phase 4).
  if (pantryRow.nutritionOverride && acceptableForResolve(pantryRow.nutritionOverride)) {
    return { nutrition: pantryRow.nutritionOverride, source: "pantry" };
  }
  const canonId = pantryRow.ingredientId || pantryRow.canonicalId || null;
  // 2. brand_nutrition row (Phase 2). Key format mirrors useBrandNutrition.
  // Exact match first (O(1) Map lookup). On a miss, fall through to a
  // word-boundary prefix match so a pantry row branded "Philadelphia"
  // still resolves against a brand_nutrition row written as
  // "Philadelphia Original". Word-boundary discipline (next char must
  // be whitespace or end-of-string) prevents misfires like "Joe's" →
  // "Trader Joe's" (fails because "Joe's" is not a prefix of "Trader
  // Joe's" from char 0). The prefix branch walks `rows` once per miss;
  // the table is small (one row per popular (canonical, brand) pair).
  if (canonId && pantryRow.brand && brandNutrition) {
    const brandKey = String(pantryRow.brand).trim().toLowerCase();
    let hit = brandNutrition.get?.(`${canonId}::${brandKey}`);
    if (!hit?.nutrition && Array.isArray(brandNutrition.rows)) {
      const candidates = brandNutrition.rows.filter(
        r => r?.canonicalId === canonId
          && r?.nutrition
          && isBrandPrefixMatch(brandKey, r.brand),
      );
      if (candidates.length) {
        // When multiple fuzzy candidates qualify, prefer the shortest
        // stored brand — a generic "Philadelphia" row beats a
        // hyper-specific "Philadelphia Original Limited Edition"
        // because the shorter string is closer to the user's input.
        candidates.sort((a, b) => (a.brand || "").length - (b.brand || "").length);
        hit = candidates[0];
      }
    }
    if (hit?.nutrition && acceptableForResolve(hit.nutrition)) {
      return { nutrition: hit.nutrition, source: "brand", brand: hit.displayBrand || pantryRow.brand };
    }
  }
  // Resolve axes once for the remaining meat-hub-aware tiers. canon
  // walks CANONICAL_ALIASES so "chicken_breast" → chicken; hubId is
  // the hub canonical id for table lookups; state/cut come from the
  // new-model pantry row first, then from the alias entry for legacy
  // compound slugs (ground_beef, chicken_breast, ribeye).
  const canon = canonId ? findIngredient(canonId) : null;
  const aliasEntry = canonId ? CANONICAL_ALIASES[canonId] : null;
  const hubId = canon?.id || aliasEntry?.base || canonId;
  const rowState = pantryRow.state || aliasEntry?.state || null;
  const rowCut   = pantryRow.cut   || aliasEntry?.cut   || null;

  // 3. ingredient_info — admin-approved per canonical. Prefer cut- or
  //    state-specific blocks when the info row supplies them (emitted
  //    by the enrich-ingredient edge function for meat hubs). State
  //    wins over cut because grinding homogenizes the animal.
  if (canonId && typeof getInfo === "function") {
    const info = getInfo(canonId);
    if (info) {
      if (rowState && info.stateNutrition?.[rowState]
          && acceptableForResolve(info.stateNutrition[rowState])) {
        return { nutrition: info.stateNutrition[rowState], source: "state" };
      }
      if (rowCut && info.cutNutrition?.[rowCut]
          && acceptableForResolve(info.cutNutrition[rowCut])) {
        return { nutrition: info.cutNutrition[rowCut], source: "cut" };
      }
      if (info.nutrition && acceptableForResolve(info.nutrition)) {
        return { nutrition: info.nutrition, source: "canonical" };
      }
    }
  }
  // 4a. State-specific registry lookup (ground beef / chicken / pork /
  //     turkey). Grinding erases the cut axis so this tier fires
  //     before cut-specific lookup.
  if (hubId && STATE_NUTRITION[hubId] && rowState) {
    const stateN = STATE_NUTRITION[hubId][rowState];
    if (stateN && acceptableForResolve(stateN)) {
      return { nutrition: stateN, source: "state" };
    }
  }
  // 4b. Cut-specific registry lookup. Falls back to DEFAULT_CUT_PER_HUB
  //     when the pantry row is an untagged hub canonical — chicken
  //     without a cut defaults to breast, matching count.toBase=200g.
  if (hubId && CUT_NUTRITION[hubId]) {
    const cut = rowCut || DEFAULT_CUT_PER_HUB[hubId] || null;
    const cutN = cut ? CUT_NUTRITION[hubId][cut] : null;
    if (cutN && acceptableForResolve(cutN)) {
      return { nutrition: cutN, source: "cut" };
    }
  }
  // 5. In-code registry fallback — fires when ingredient_info hasn't
  //    been seeded for this canonical yet. Reuses `canon` resolved
  //    above so we don't double-walk the alias map.
  if (canon?.nutrition && acceptableForResolve(canon.nutrition)) {
    return { nutrition: canon.nutrition, source: "default" };
  }
  return { nutrition: null, source: null };
}

// Read-side gate. Stricter than hasNumbers — even if a row slipped
// past the write-side `validateNutrition` (old data, a direct SQL edit,
// a future OFF ingestion path we haven't gated yet), the resolver
// pretends the tier is empty and falls through to the next one. The
// tolerant fallback in `scaleFactor` still handles the legacy
// `per="1 tsp (5g)"` shape separately, so we deliberately DON'T bounce
// those here — bouncing legacy rows would strand existing DB data the
// user hasn't had a chance to re-enrich yet. We only refuse shapes
// that the scaleFactor can't interpret under any branch.
function acceptableForResolve(n) {
  if (!hasNumbers(n)) return false;
  // Modern enum is always fine.
  if (n.per === "100g" || n.per === "count") return true;
  if (n.per === "serving") {
    const sg = Number(n.serving_g);
    return Number.isFinite(sg) && sg > 0;
  }
  // Legacy free-text "(Ng)" shape — scaleFactor salvages it.
  if (typeof n.per === "string" && /\(\s*[\d.]+\s*g\s*\)/i.test(n.per)) return true;
  // Anything else is garbage — skip the tier.
  return false;
}

// Word-boundary prefix match on two already-lowercased brand strings.
// One must be a prefix of the other, with the break point at a word
// boundary so we never match mid-word (e.g. "Phil" → "Philadelphia").
// Intentionally symmetric: both "stored shorter than input" and "input
// shorter than stored" match, since either ordering can be the
// generic-vs-specific case depending on which end has more data.
function isBrandPrefixMatch(a, b) {
  if (!a || !b) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (!longer.startsWith(shorter)) return false;
  if (longer.length === shorter.length) return true; // exact (Map.get handles; safety net here)
  return /\s/.test(longer[shorter.length]);
}

function hasNumbers(n) {
  if (!n || typeof n !== "object") return false;
  // At least one macro has to be a real number; otherwise there's
  // nothing to render and the UI should treat it as missing.
  return ["kcal", "protein_g", "fat_g", "carb_g"].some(k => typeof n[k] === "number");
}

// Strict sanity check on a nutrition block before it's written to the
// DB (or trusted at read time). Rejects lazy / malformed entries that
// would otherwise silently inflate the dashboard:
//   - wrong `per` value (not in the enum)
//   - per="serving" without a positive numeric serving_g
//   - kcal missing, non-finite, negative, or absurdly large (>10000 per
//     base unit — a stick of butter is 813 kcal, dense fat is 900; 10k
//     is unreachable by real food, so it's a safe ceiling)
//   - any macro that's present but not a finite non-negative number
//
// Side effect: MUTATES the block in-place to normalize alias fields.
// The label scanner returns FDA-style keys (`total_fat_g`,
// `total_sugar_g`), but the rest of the app reads the legacy `fat_g`
// / `sugar_g` names. Mirroring them here means downstream consumers
// (formatMacros, resolveNutrition, MacroCell, recipeNutrition) keep
// working unmodified — new label-scan rows drop straight into the
// existing renderers without per-consumer plumbing. Legacy rows
// that only have `fat_g` / `sugar_g` round-trip unchanged.
//
// Also: cross-check that added_sugar_g does not exceed total_sugar_g
// (+ 0.5g for label rounding tolerance) — "Includes Xg Added Sugars"
// is a subset of "Total Sugars" on every real FDA label.
//
// Returns { ok: true } on pass or { ok: false, reason } on failure. Used
// by useBrandNutrition.upsert and the pantry-override write path to
// reject bad data at the boundary rather than propagating it.
export function validateNutrition(n) {
  if (!n || typeof n !== "object") return { ok: false, reason: "not an object" };

  // Alias normalization — see fn docblock for rationale.
  // Mirror total_fat_g ↔ fat_g and total_sugar_g ↔ sugar_g so either
  // key resolves. Prefer the "total_" FDA key when both are present
  // and disagree (assume the label-scan value is more recent).
  if (n.total_fat_g != null && n.fat_g == null) n.fat_g = n.total_fat_g;
  if (n.fat_g != null && n.total_fat_g == null) n.total_fat_g = n.fat_g;
  if (n.total_sugar_g != null && n.sugar_g == null) n.sugar_g = n.total_sugar_g;
  if (n.sugar_g != null && n.total_sugar_g == null) n.total_sugar_g = n.sugar_g;

  const per = n.per;
  if (per !== "100g" && per !== "count" && per !== "serving") {
    return { ok: false, reason: `per must be "100g"|"count"|"serving", got ${JSON.stringify(per)}` };
  }
  if (per === "serving") {
    const sg = Number(n.serving_g);
    if (!Number.isFinite(sg) || sg <= 0 || sg > 5000) {
      return { ok: false, reason: `serving_g required and must be 0-5000g when per="serving", got ${JSON.stringify(n.serving_g)}` };
    }
  }
  const kcal = Number(n.kcal);
  if (!Number.isFinite(kcal) || kcal < 0 || kcal > 10000) {
    return { ok: false, reason: `kcal must be 0-10000, got ${JSON.stringify(n.kcal)}` };
  }
  // Ceilings tuned to ~2-4x the worst real-food value so bogus
  // scans (missed decimal point, mg / g confusion) bounce but
  // legitimate dense foods fit. Aliases (fat_g, total_fat_g) are
  // both listed; the normalization above keeps them in sync.
  const MACRO_CEILINGS = {
    // Legacy 7-field set — kept for back-compat.
    protein_g: 200,  fat_g: 200,  carb_g: 200,
    fiber_g: 100,    sugar_g: 200,
    sodium_mg: 50000,
    // FDA-label additions (label-scan / manual detailed entry).
    total_fat_g: 200, saturated_fat_g: 100, trans_fat_g: 50,
    cholesterol_mg: 5000,
    total_sugar_g: 200, added_sugar_g: 200,
    vitamin_d_mcg: 500, calcium_mg: 5000, iron_mg: 200, potassium_mg: 10000,
  };
  for (const [key, ceiling] of Object.entries(MACRO_CEILINGS)) {
    if (n[key] == null) continue;
    const v = Number(n[key]);
    if (!Number.isFinite(v) || v < 0 || v > ceiling) {
      return { ok: false, reason: `${key} must be 0-${ceiling}, got ${JSON.stringify(n[key])}` };
    }
  }
  // Cross-check: added sugars can't exceed total sugars (with
  // 0.5g slack for label rounding).
  if (
    typeof n.added_sugar_g === "number" &&
    typeof n.total_sugar_g === "number" &&
    n.added_sugar_g > n.total_sugar_g + 0.5
  ) {
    return {
      ok: false,
      reason: `added_sugar_g (${n.added_sugar_g}) exceeds total_sugar_g (${n.total_sugar_g})`,
    };
  }
  return { ok: true };
}

// Scale factor for applying a nutrition block to a quantity. Returns
// null when the conversion isn't possible (unknown unit, missing
// serving_g on a per-serving nutrition, etc.) — callers should
// treat that as a coverage gap rather than a zero.
//
// Every mass-based canonical's ladder declares `toBase` in GRAMS (butter,
// flour, spices — see the spice factory in src/data/ingredients.js for the
// gram-base convention). Count-based canonicals (eggs, apples, bananas)
// declare `toBase=1` per count. The nutrition.per value picks which axis
// we're scaling along.
//
// opts.countWeightG (optional) — per-row grams-per-count. Two distinct
// roles depending on the canonical's ladder shape:
//   - Mass ladder (chicken_breast, sausage): overrides the canonical's
//     default count.toBase so "each breast in THIS pack is 170g"
//     calibrates downstream math. Delegated to convertWithBridge via
//     the synthetic row so the same override logic powers CookComplete.
//   - Count-only ladder (tortillas, bread_slice): acts as the MISSING
//     grams anchor. The canonical has no `g:1` entry so convertWithBridge's
//     pure-bridge path can't resolve count→g (the mass-side unit isn't
//     in the ladder). Compute grams directly from counts × override.
//     Without the override we still bail — no grams axis to scale along.
export function scaleFactor(qty, canonical, nutrition, opts = {}) {
  if (!qty || !canonical || !nutrition) return null;

  // Tolerant fallback — pre-v3 AI enrichment wrote free-text into
  // `per` ("1 tsp (5g)", "1 medium apple (182g)"). The gram weight is
  // right there in parentheses; salvage it so rows already persisted
  // in production still resolve instead of silently scoring zero.
  // Forward-going, the edge function strips malformed blocks before
  // they land — this branch only matters for the migration window.
  const per = coercePer(nutrition);
  if (!per) return null;

  const rowGramsPerCount = Number(opts.countWeightG);
  const haveOverride = Number.isFinite(rowGramsPerCount) && rowGramsPerCount > 0;
  const massLadder = isMassLadder(canonical);

  // Mass-ladder path: delegate qty→grams to convertWithBridge. Pass
  // the override through synthRow on count units so the row's
  // calibrated weight wins over the canonical's baked-in count.toBase.
  const synthRow = haveOverride && massLadder && qty.unit === "count"
    ? { countWeightG: rowGramsPerCount }
    : null;

  // Count-only ladder + override: roll qty up to counts via the
  // canonical's own ladder (pack.toBase=10 counts, count.toBase=1),
  // then multiply by grams-per-count to land on grams.
  // convertWithBridge's pure-bridge path can't do this because it
  // requires the mass-side unit to exist in the canonical's ladder,
  // which tortillas / bread_slice don't declare.
  let countOnlyGrams = null;
  if (haveOverride && !massLadder) {
    const fromEntry = canonical.units?.find(u => u.id === qty.unit);
    const countEntry = canonical.units?.find(u => u.id === "count");
    if (fromEntry && countEntry) {
      const counts = Number(qty.amount) * Number(fromEntry.toBase) / Number(countEntry.toBase);
      if (Number.isFinite(counts)) countOnlyGrams = counts * rowGramsPerCount;
    }
  }

  // Ladder-local shortcut: when qty.unit IS in the canonical's
  // ladder, its `toBase` value is the canonical's base unit. For
  // mass-anchored ladders (any registry mass unit present — g, kg,
  // oz, lb), that base is grams. For count-only ladders (bread:
  // slice toBase=1, loaf toBase=20), it's counts. Without this,
  // convertStrict's slice→g path fails on canonicals that don't
  // declare an explicit `g` anchor (prosciutto, ham, bacon, salami)
  // — they all use slice with toBase set to grams but have no
  // ladder entry for grams itself, so convertWithBridge falls
  // through to universal and rejects the unknown ladder-local unit.
  const fromLadder = findLadderLocal(canonical, qty.unit);
  const ladderBaseGrams = fromLadder && hasMassAnchor(canonical)
    ? Number(qty.amount) * Number(fromLadder.toBase)
    : null;
  const ladderBaseCounts = fromLadder && !hasMassAnchor(canonical)
    ? Number(qty.amount) * Number(fromLadder.toBase)
    : null;

  switch (per.kind) {
    case "100g": {
      if (Number.isFinite(ladderBaseGrams)) return ladderBaseGrams / 100;
      if (countOnlyGrams != null) return countOnlyGrams / 100;
      if (!massLadder) return null;
      const res = bridgeToGrams(qty, canonical, synthRow);
      return res.ok ? res.value / 100 : null;
    }
    case "count": {
      if (Number.isFinite(ladderBaseCounts)) return ladderBaseCounts;
      const res = convertWithBridge(qty, "count", canonical, synthRow);
      return res.ok ? res.value : null;
    }
    case "serving": {
      const g = per.serving_g;
      if (!Number.isFinite(g) || g <= 0) return null;
      if (Number.isFinite(ladderBaseGrams)) return ladderBaseGrams / g;
      if (countOnlyGrams != null) return countOnlyGrams / g;
      if (!massLadder) return null;
      const res = bridgeToGrams(qty, canonical, synthRow);
      return res.ok ? res.value / g : null;
    }
    default:
      return null;
  }
}

// Lower-cased ladder lookup. Doesn't normalize the unit through
// the registry — we want to match ladder-local IDs verbatim
// (slice / stick / wedge / loaf / bottle).
function findLadderLocal(canonical, unit) {
  if (!canonical?.units || unit == null) return null;
  const u = String(unit).toLowerCase();
  for (const e of canonical.units) {
    if (String(e.id).toLowerCase() === u) return e;
  }
  return null;
}

// True iff the canonical's ladder declares ANY mass-family registry
// unit (g/kg/oz/lb). When true, the ladder's toBase values represent
// grams, so a ladder-local unit like "slice" with toBase=10 means
// "1 slice = 10 grams." Without a mass anchor, the toBase values
// represent counts (bread: slice=1, loaf=20).
function hasMassAnchor(canonical) {
  if (!canonical?.units) return false;
  for (const u of canonical.units) {
    const n = normalizeUnitId(u.id);
    if (n === "g" || n === "kg" || n === "oz" || n === "lb") return true;
  }
  return false;
}

// Volume → mass bridge with a "water density" (1.0 g/ml) safety net.
// convertWithBridge requires explicit density_g_per_ml on the
// canonical; without it, every volume-anchored canonical (white
// wine, broth, juice, milk, lemon juice, beer, etc.) silently
// scores zero on the rollup.
//
// 1.0 g/ml is within ~5% for nearly every cooking liquid — wines
// 0.98–0.99, broths ~1.0, juices ~1.04, vinegar ~1.01, beer ~1.0,
// soft drinks ~1.04. Notable outliers worth seeding density on:
// oils ~0.92, honey/molasses/syrups 1.35–1.43. For those, set
// `density_g_per_ml` explicitly on the canonical and the explicit
// value wins (readDensity prefers explicit).
function bridgeToGrams(qty, canonical, synthRow) {
  const first = convertWithBridge(qty, "g", canonical, synthRow);
  if (first.ok) return first;
  if (first.reason !== "no-density") return first;
  const fromUnit = normalizeUnitId(qty.unit);
  const isVolume = ["ml","l","cup","tbsp","tsp","fl_oz","pint","quart","gallon"].includes(fromUnit);
  if (!isVolume) return first;
  const fallback = { ...canonical, density_g_per_ml: 1.0 };
  return convertWithBridge(qty, "g", fallback, synthRow);
}

// Normalize nutrition.per into a structured shape the switch above can
// consume. Handles the modern enum {"100g","count","serving"} and the
// legacy free-text format ("1 tsp (5g)", "1 medium apple (182g)")
// written by pre-v3 AI enrichments. The free-text fallback reads as
// "per=serving with serving_g=N" — the gram weight is the canonical
// reference; the leading "1 tsp" / "1 medium apple" is flavor text for
// humans, not a conversion instruction.
function coercePer(nutrition) {
  const raw = nutrition?.per;
  if (raw === "100g")    return { kind: "100g" };
  if (raw === "count")   return { kind: "count" };
  if (raw === "serving") return { kind: "serving", serving_g: Number(nutrition.serving_g) };
  if (typeof raw === "string") {
    // Match a trailing "(<number><optional space>g)" group.
    const m = raw.match(/\(\s*([\d.]+)\s*g\s*\)/i);
    if (m) {
      const g = Number(m[1]);
      if (Number.isFinite(g) && g > 0) return { kind: "serving", serving_g: g };
    }
    if (/^\s*100\s*g\s*$/i.test(raw)) return { kind: "100g" };
  }
  return null;
}

// Re-exported from unitConvert to preserve every existing
// `import { effectiveCountWeightG } from "./nutrition"` call site.
// Single source of truth for the math lives alongside convertWithBridge
// in unitConvert.js — they share the countWeightG resolution logic
// so fixing grams-per-count rules in one spot propagates everywhere.
// Module-scope binding via the top-of-file import lets scaleFactor
// below use the same function without a second import.
export { effectiveCountWeightG, isMassLadder };

// Parse a free-text amount string into { amount, unit } against a
// canonical's ladder. Handles unicode fractions ("½ cup"), mixed
// fractions ("1½ tbsp"), and common English unit aliases. Returns
// null for anything we can't confidently parse — the coverage ratio
// surfaces that honestly instead of guessing.
const UNICODE_FRACTIONS = {
  "¼": 0.25, "½": 0.5, "¾": 0.75,
  "⅓": 1/3, "⅔": 2/3,
  "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875,
};
// Semantic size/count words — NOT unit aliases. These are English
// shortcuts that mean "one unit of this countable thing" regardless
// of the thing's actual ladder entry (which is almost always just
// "count"). Kept LOCAL to this module because they're recipe-parse
// semantics, not the measurement registry's concern:
//   "1 large onion"     → count:1
//   "2 sprigs thyme"    → count:2
//   "1 package cheddar" → count:1   (only if ladder has count)
//
// Pure unit aliases (tablespoon, teaspoon, gram, oz, etc.) live in
// src/lib/units/aliases.js and are resolved here via normalizeUnitId.
const SEMANTIC_COUNT_WORDS = new Set([
  "large", "small", "medium", "xl",
  "clove", "cloves", "sprig", "sprigs", "stalk", "stalks",
  "head", "heads", "whole",
  "link", "links", "pack", "packs", "package", "packages",
  "pkg", "pkgs", "slice", "slices", "patty", "patties",
]);

export function parseAmountString(str, canonical) {
  if (!str || !canonical) return null;
  if (typeof str !== "string") return null;
  const trimmed = str.trim().toLowerCase();
  // Pull off any leading numeric — decimal, integer, unicode fraction,
  // or an integer immediately followed by a unicode fraction ("1½").
  const m = trimmed.match(/^(\d+(?:\.\d+)?)?\s*([¼½¾⅓⅔⅛⅜⅝⅞])?\s*(.*)$/);
  if (!m) return null;
  const intPart  = m[1] ? Number(m[1]) : 0;
  const fracPart = m[2] ? UNICODE_FRACTIONS[m[2]] : 0;
  const amount = intPart + fracPart;
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const rest = (m[3] || "").trim();
  // Candidate unit token. Multi-word units ("fl oz", "fluid ounce")
  // must NOT be peeled to their first token ("fl") — that loses the
  // unit and the parse falls back to the canonical's default, which
  // broke cup→fl oz→cup round-trips ("48 fl oz" came back as "48
  // cups"). Try a 2-token unit that normalizes to a real id first,
  // then the first token.
  const restToks = rest.split(/[\s,.;:]+/).filter(Boolean);
  const multiWordUnit = restToks.length >= 2
    ? normalizeUnitId(restToks.slice(0, 2).join(" "))
    : null;
  const unitToken = multiWordUnit || (restToks[0] || "").replace(/[^a-z]/g, "");
  // Resolution order:
  //   1. Real unit via the shared registry (tbsp, oz, ml, …).
  //   2. Semantic count word ("large", "sprig", "package", …) → "count".
  //   3. Fallback to canonical's defaultUnit.
  // Resolution order:
  //   1. Ladder-local match — "slice" / "slices" against the
  //      canonical's own units[]. Many canonicals carry ladder-only
  //      units that don't live in the universal registry (slice,
  //      stick, wedge, clove, loaf, bottle). When the qty token
  //      matches one of these, use the ladder entry directly so
  //      scaleFactor can read its toBase grams without a bridge.
  //      This is what makes "1 slice prosciutto" resolve via the
  //      ladder's `slice.toBase=10` instead of dropping to a
  //      semantic-count fallback that loses the per-slice weight.
  //   2. Registry unit (tbsp, oz, ml, …) — universal, scaleFactor
  //      uses density/family bridges.
  //   3. Semantic count word (link, package, patty, …) — collapses
  //      to "count" and routes through the count-bridge path.
  //   4. Canonical's defaultUnit — last-resort fallback for amount
  //      strings that omitted the unit ("1 buttermilk" → defaults
  //      to whatever the canonical declares).
  let unitId = null;
  let isUniversal = false;
  // 1. Ladder-local
  if (canonical.units && unitToken) {
    const singular = unitToken.endsWith("s") ? unitToken.slice(0, -1) : unitToken;
    for (const u of canonical.units) {
      const idLower = String(u.id).toLowerCase();
      if (idLower === unitToken || idLower === singular) {
        unitId = u.id;
        isUniversal = true;   // ladder is its own validation
        break;
      }
      if (Array.isArray(u.aliases)) {
        for (const a of u.aliases) {
          const aLower = String(a).toLowerCase();
          if (aLower === unitToken || aLower === singular) {
            unitId = u.id;
            isUniversal = true;
            break;
          }
        }
        if (unitId) break;
      }
    }
  }
  // 2. Registry
  if (!unitId) {
    const reg = normalizeUnitId(unitToken);
    if (reg) {
      unitId = reg;
      isUniversal = true;
    }
  }
  // 3. Semantic count
  if (!unitId && SEMANTIC_COUNT_WORDS.has(unitToken)) {
    unitId = "count";
    isUniversal = true;
  }
  // 4. Canonical default
  if (!unitId) unitId = canonical.defaultUnit || null;
  if (!unitId) return null;
  // Only require the canonical's ladder to declare this unit when
  // we couldn't validate it via the universal registry.
  if (!isUniversal) {
    const hasUnit = canonical.units?.some(u => normalizeUnitId(u.id) === normalizeUnitId(unitId));
    if (!hasUnit) return null;
  }
  return { amount, unit: unitId };
}

// Compute nutrition for one recipe. Sums macros across every
// ingredient that we can both (a) resolve to a canonical with
// nutrition AND (b) convert its amount into the canonical's per-unit
// base. Everything else counts as a coverage gap.
//
// Returns:
//   total        — sum across the whole recipe
//   perServing   — total / recipe.serves (floor 1)
//   coverage     — { resolved, total } so the UI can disclose gaps
//                  honestly ("based on 7 of 9 ingredients")
export function recipeNutrition(recipe, { pantry = [], brandNutrition, getInfo } = {}) {
  const totals = { kcal: 0, protein_g: 0, fat_g: 0, carb_g: 0, fiber_g: 0, sodium_mg: 0, sugar_g: 0 };
  let resolved = 0;
  const ingredients = recipe?.ingredients || [];
  for (const ing of ingredients) {
    const canonicalId = resolveIngredientCanonical(ing);
    if (!canonicalId) continue;
    const canon = findIngredient(canonicalId);
    if (!canon) continue;
    // Prefer a matching pantry row so brand + override apply. Fall
    // back to a canonical-only shell when the user hasn't stocked it
    // (recipes can reference ingredients the pantry doesn't carry).
    const pantryRow = pantry.find(p => (p.ingredientId || p.canonicalId) === canonicalId)
      || { ingredientId: canonicalId, brand: null, nutritionOverride: null };
    // Prefer structured qty when Claude / the registry stamped it;
    // otherwise parse the free-text amount. Recipe rows shaped like
    // `{ amount: "2 tbsp", qty: { amount: 2, unit: "tbsp" } }` are
    // common for bundled recipes; AI drafts typically only set
    // `amount`.
    const qty = ing.qty || parseAmountString(ing.amount, canon);
    if (!qty) continue;
    const countWeightG = effectiveCountWeightG(pantryRow, canon);
    // Walk the resolver tiers and use the FIRST one whose nutrition
    // can actually be scaled to the recipe's amount. Without this
    // fallback, a brand_nutrition row with an awkward `per` unit
    // (e.g. per-serving with no serving_g) would lock the row out
    // of the rollup even though canonical/ingredient_info has clean
    // per-100g data the same canonical's ItemCard renders.
    const candidate = resolveScalableNutrition(pantryRow, canon, qty, countWeightG, { brandNutrition, getInfo });
    if (!candidate) continue;
    const { nutrition, factor } = candidate;
    for (const key of Object.keys(totals)) {
      if (typeof nutrition[key] === "number") totals[key] += nutrition[key] * factor;
    }
    resolved++;
  }
  const serves = Math.max(1, Number(recipe?.serves) || 1);
  const perServing = Object.fromEntries(
    Object.entries(totals).map(([k, v]) => [k, v / serves])
  );
  return {
    total: round(totals),
    perServing: round(perServing),
    coverage: { resolved, total: ingredients.length },
  };
}

// Walk the nutrition resolver cascade and return the FIRST tier whose
// nutrition can be scaled to the requested qty. Without this, a brand
// row with an unscalable `per` unit (e.g. per-serving without
// serving_g) blocks the canonical/ingredient_info fallback even when
// the canonical's data IS scalable. The user's mental model: "I see
// kcal on the ItemCard, why don't I see it on the recipe?" — this
// closes that gap.
//
// Tiers tried in order:
//   1. brand-tier (from resolveNutrition first call)
//   2. ingredient_info via getInfo(canonicalId).nutrition
//   3. in-code canonical default (canon.nutrition)
function resolveScalableNutrition(pantryRow, canon, qty, countWeightG, { brandNutrition, getInfo } = {}) {
  const canonicalId = pantryRow?.ingredientId || pantryRow?.canonicalId || canon?.id || null;
  // Collect candidates in order. Each entry: { nutrition, source }.
  const candidates = [];
  // 1. The full resolveNutrition cascade (override → brand → info →
  // state → cut → default). One tier wins.
  const primary = resolveNutrition(pantryRow, { brandNutrition, getInfo });
  if (primary?.nutrition) candidates.push(primary);
  // 2. ingredient_info as an explicit fallback when the primary was
  // brand-tier (or override). Skipped if the primary already came
  // from info / canonical / state / cut — those would just retry
  // the same data.
  if (
    canonicalId
    && typeof getInfo === "function"
    && (primary?.source === "brand" || primary?.source === "pantry")
  ) {
    const info = getInfo(canonicalId);
    if (info?.nutrition && acceptableForResolve(info.nutrition)) {
      candidates.push({ nutrition: info.nutrition, source: "canonical-fallback" });
    }
  }
  // 3. In-code canonical default — last-line fallback for when neither
  // pantry brand nor ingredient_info has scalable data.
  if (canon?.nutrition && acceptableForResolve(canon.nutrition)) {
    if (!candidates.some(c => c.nutrition === canon.nutrition)) {
      candidates.push({ nutrition: canon.nutrition, source: "default-fallback" });
    }
  }
  for (const c of candidates) {
    const factor = scaleFactor(qty, canon, c.nutrition, { countWeightG });
    if (factor != null) return { nutrition: c.nutrition, factor, source: c.source };
  }
  return null;
}

// DEBUG: Per-ingredient breakdown of recipeNutrition. Same resolver
// chain, but returns one row per recipe ingredient annotated with what
// happened — canonical match, parsed qty, nutrition source, scale
// factor, macro contribution, and a `reason` when a row didn't
// contribute. Used by the CookComplete debug screen to expose where
// calories are being silently dropped. Safe to delete once the
// dashboard is trusted.
export function recipeNutritionBreakdown(recipe, { pantry = [], brandNutrition, getInfo } = {}) {
  const ingredients = recipe?.ingredients || [];
  const items = [];
  const totals = { kcal: 0, protein_g: 0, fat_g: 0, carb_g: 0, fiber_g: 0, sodium_mg: 0, sugar_g: 0 };
  let resolved = 0;
  for (const ing of ingredients) {
    const resolvedCanonicalId = resolveIngredientCanonical(ing);
    const row = {
      name: ing.item || resolvedCanonicalId || "(unnamed)",
      canonicalId: resolvedCanonicalId || null,
      canonicalIdSource: ing.ingredientId ? "explicit" : (resolvedCanonicalId ? "inferred-from-name" : null),
      amount: ing.amount || null,
      qty: ing.qty || null,
      parsedQty: null,
      source: null,
      brand: null,
      factor: null,
      nutrition: null,
      macros: null,
      kcal: 0,
      reason: null,
    };
    if (!resolvedCanonicalId) { row.reason = "no canonical id and name didn't resolve"; items.push(row); continue; }
    const canon = findIngredient(resolvedCanonicalId);
    if (!canon) { row.reason = `canonical "${resolvedCanonicalId}" not found in registry`; items.push(row); continue; }
    row.canonical = canon.name;
    const pantryRow = pantry.find(p => (p.ingredientId || p.canonicalId) === resolvedCanonicalId)
      || { ingredientId: resolvedCanonicalId, brand: null, nutritionOverride: null };
    const parsed = ing.qty || parseAmountString(ing.amount, canon);
    row.parsedQty = parsed;
    if (!parsed) {
      row.reason = `could not parse amount "${ing.amount}"`;
      // Still surface what the brand-tier resolver returned so the
      // diagnostic shows whether identity resolved even though
      // amount didn't.
      const probe = resolveNutrition(pantryRow, { brandNutrition, getInfo });
      row.source = probe?.source || null;
      row.brand = probe?.brand || null;
      row.nutrition = probe?.nutrition || null;
      items.push(row);
      continue;
    }
    const countWeightG = effectiveCountWeightG(pantryRow, canon);
    row.countWeightG = countWeightG;
    // Walk fallback tiers — same logic as recipeNutrition. brand →
    // ingredient_info → canonical default. Whichever produces a
    // valid scaleFactor wins.
    const candidate = resolveScalableNutrition(pantryRow, canon, parsed, countWeightG, { brandNutrition, getInfo });
    if (!candidate) {
      // No tier scaled. Probe just for the diagnostic so the user
      // sees what was tried.
      const probe = resolveNutrition(pantryRow, { brandNutrition, getInfo });
      row.source = probe?.source || null;
      row.brand = probe?.brand || null;
      row.nutrition = probe?.nutrition || null;
      // Annotate every tier so the diagnostic can pinpoint exactly
      // where the data is (or isn't). When the ItemCard renders
      // nutrition but the recipe doesn't, this row tells you which
      // tier the card is reading from vs. which tier the recipe is
      // failing on.
      row.diag = {
        pantryRowFound: !!pantry.find(p => (p.ingredientId || p.canonicalId) === resolvedCanonicalId),
        pantryRowBrand: pantryRow?.brand || null,
        pantryHasOverride: !!pantryRow?.nutritionOverride,
        canonicalInfoFound: typeof getInfo === "function" ? !!getInfo(resolvedCanonicalId) : null,
        canonicalInfoHasNutrition: typeof getInfo === "function" ? !!getInfo(resolvedCanonicalId)?.nutrition : null,
        canonicalDefaultNutrition: !!canon?.nutrition,
      };
      if (!probe?.nutrition) {
        row.reason = "no nutrition from any resolver tier";
      } else {
        const missingGrams =
          (probe.nutrition.per === "100g" || probe.nutrition.per === "serving") &&
          parsed.unit === "count" &&
          !(Number.isFinite(countWeightG) && countWeightG > 0);
        row.reason = missingGrams
          ? `per="${probe.nutrition.per}" needs grams-per-count — set "each ~g" on the pantry row`
          : `scaleFactor returned null on every tier (per="${probe.nutrition.per}", unit="${parsed.unit}")`;
      }
      items.push(row);
      continue;
    }
    const { nutrition, factor, source } = candidate;
    row.source = source;
    row.nutrition = nutrition;
    row.factor = factor;
    const macros = {};
    for (const key of Object.keys(totals)) {
      if (typeof nutrition[key] === "number") {
        const v = nutrition[key] * factor;
        macros[key] = v;
        totals[key] += v;
      }
    }
    row.macros = macros;
    row.kcal = macros.kcal || 0;
    resolved++;
    items.push(row);
  }
  const serves = Math.max(1, Number(recipe?.serves) || 1);
  const perServing = Object.fromEntries(
    Object.entries(totals).map(([k, v]) => [k, v / serves])
  );
  return {
    items,
    total: totals,
    perServing,
    serves,
    coverage: { resolved, total: ingredients.length },
  };
}

// Compute nutrition for a MEAL — sum of perServing across pieces.
// A meal of { main-for-2 + side-for-4 } is still "meal for 2" — each
// piece contributes its own perServing macros; the resulting totals
// represent one eater's share of the whole meal.
export function mealNutrition(meal, { pantry = [], brandNutrition, getInfo } = {}) {
  const totals = { kcal: 0, protein_g: 0, fat_g: 0, carb_g: 0, fiber_g: 0, sodium_mg: 0, sugar_g: 0 };
  let resolvedPieces = 0;
  let totalIngredientsResolved = 0;
  let totalIngredients = 0;
  const pieces = meal?.pieces || [];
  for (const p of pieces) {
    const r = p?.recipe;
    if (!r) continue;
    const n = recipeNutrition(r, { pantry, brandNutrition, getInfo });
    if (n.coverage.total === 0) continue;
    for (const key of Object.keys(totals)) {
      if (typeof n.perServing[key] === "number") totals[key] += n.perServing[key];
    }
    if (n.coverage.resolved > 0) resolvedPieces++;
    totalIngredientsResolved += n.coverage.resolved;
    totalIngredients        += n.coverage.total;
  }
  return {
    total: round(totals),
    coverage: {
      pieces:      { resolved: resolvedPieces,        total: pieces.length },
      ingredients: { resolved: totalIngredientsResolved, total: totalIngredients },
    },
  };
}

// Compute per-unit nutrition for a single pantry row, scaled to its
// row.amount + row.unit. Used by the ItemCard chip to answer "how
// many kcal in this jar?" without reference to any recipe. Returns
// the raw source + scaled numbers; UI picks which to show.
export function pantryItemNutrition(pantryRow, { brandNutrition, getInfo } = {}) {
  const { nutrition, source, brand } = resolveNutrition(pantryRow, { brandNutrition, getInfo });
  if (!nutrition) return { nutrition: null, source: null };
  return { nutrition, source, brand };
}

// Round every value in a macros object to 1 decimal place for display.
// Keeps the total-rollup honest internally (we sum raw floats) but
// surfaces reasonable numbers instead of "712.39428 kcal".
function round(macros) {
  const out = {};
  for (const [k, v] of Object.entries(macros || {})) {
    out[k] = Number.isFinite(v) ? Math.round(v * 10) / 10 : 0;
  }
  return out;
}

// Compact "142 kcal · 12p · 8c · 7f" display string. Used by ItemCard
// chip + recipe summary line. Drops zero macros to save space; always
// leads with kcal.
export function formatMacros(macros, { verbose = false } = {}) {
  if (!macros) return "—";
  const kcal = Math.round(macros.kcal || 0);
  const p = Math.round(macros.protein_g || 0);
  const c = Math.round(macros.carb_g    || 0);
  const f = Math.round(macros.fat_g     || 0);
  if (verbose) {
    return `${kcal} kcal · ${p}g protein · ${c}g carbs · ${f}g fat`;
  }
  return `${kcal} kcal · ${p}p · ${c}c · ${f}f`;
}

// Ingredients + allergens resolver — same two-tier cascade as the
// nutrition resolver, minus the canonical tiers (ingredient lists
// are brand-specific — "Kerrygold Butter" and "Land O'Lakes Butter"
// have different ingredient statements, so averaging across canonicals
// would be wrong).
//
// Reads per-jar first (pantry_items.nutrition_override jsonb carries
// `ingredients_text` and `allergens` as sibling keys to the macros
// block), then brand_nutrition's first-class columns.
//
// Returns { ingredientsText, allergens, source }:
//   - ingredientsText: string | null  — raw declaration, as printed
//   - allergens: string[] | null      — parsed "Contains:" line
//   - source: "pantry" | "brand" | null
export function resolveIngredients(pantryRow, { brandNutrition } = {}) {
  if (!pantryRow) return { ingredientsText: null, allergens: null, source: null };
  // Tier 1 — per-jar override. Fields ride on the same jsonb block
  // as macros; sibling keys next to kcal / protein_g / etc.
  const override = pantryRow.nutritionOverride;
  if (override && typeof override === "object") {
    const text = typeof override.ingredients_text === "string" && override.ingredients_text.trim()
      ? override.ingredients_text.trim() : null;
    const all = Array.isArray(override.allergens) && override.allergens.length
      ? override.allergens : null;
    if (text || all) return { ingredientsText: text, allergens: all, source: "pantry" };
  }
  // Tier 2 — brand_nutrition shared row. Same prefix-matching rules
  // as resolveNutrition (exact key first, word-boundary prefix fallback).
  const canonId = pantryRow.ingredientId || pantryRow.canonicalId || null;
  if (canonId && pantryRow.brand && brandNutrition) {
    const brandKey = String(pantryRow.brand).trim().toLowerCase();
    let hit = brandNutrition.get?.(`${canonId}::${brandKey}`);
    if (!(hit?.ingredientsText || hit?.allergens) && Array.isArray(brandNutrition.rows)) {
      const candidates = brandNutrition.rows.filter(
        r => r?.canonicalId === canonId
          && (r?.ingredientsText || r?.allergens)
          && isBrandPrefixMatchForIngredients(brandKey, r.brand),
      );
      if (candidates.length) {
        candidates.sort((a, b) => (a.brand || "").length - (b.brand || "").length);
        hit = candidates[0];
      }
    }
    if (hit && (hit.ingredientsText || hit.allergens)) {
      return {
        ingredientsText: hit.ingredientsText || null,
        allergens:       hit.allergens || null,
        source:          "brand",
      };
    }
  }
  return { ingredientsText: null, allergens: null, source: null };
}

// Same shape as isBrandPrefixMatch above — inlined here to avoid
// exporting it twice. Kept local to the ingredients resolver so
// future reshuffles don't accidentally break the nutrition resolver's
// matching rules.
function isBrandPrefixMatchForIngredients(a, b) {
  if (!a || !b) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (!longer.startsWith(shorter)) return false;
  if (longer.length === shorter.length) return true;
  return /\s/.test(longer[shorter.length]);
}

// Source badge label + color. Keeps the hierarchy legible on the UI
// without the user having to guess where a number came from.
//
// "pantry" tier was previously labeled "YOU" — accurate but reads as
// "this is just your local data." Relabeled to "VERIFIED" with the
// sky-blue accent so the user feels they've made the data OFFICIAL
// (the scan-then-save flow IS a verification pass — a human
// confirmed every field against the physical label). Callers render
// an animated checkmark alongside the label via <VerifiedMark>. The
// color + icon hint tells the chip to render the check.
export function sourceBadge(source) {
  switch (source) {
    case "pantry":     return { label: "VERIFIED", color: "#7eb8d4", icon: "check" };
    case "brand":      return { label: "BRAND",    color: "#c7a8d4" };
    case "canonical":  return { label: "CANONICAL", color: "#b8a878" };
    case "cut":        return { label: "CUT",      color: "#a8553a" };
    case "state":      return { label: "STATE",    color: "#c7a8d4" };
    case "default":    return { label: "EST.",     color: "#888"    };
    // label_scan — provenance for scan-nutrition-label writes. Gold
    // accents it alongside the "📸" affordance used in the scanner UI.
    case "label_scan": return { label: "SCAN",     color: "#f5c842" };
    default:           return { label: "",         color: "#555"    };
  }
}
